import type { StateCreator } from 'zustand';
import type {
  KanbanBoard,
  KanbanCard,
  KanbanStatus,
  KanbanConfig,
  KanbanFilter,
  KanbanStatistics,
  KanbanPriority,
  KanbanChecklistItem,
  DragResult,
  KanbanColumn,
} from '../../types/kanban';
import type { Resource } from '../../types';
import { DEFAULT_KANBAN_CONFIG, KANBAN_COLUMNS } from '../../types/kanban';

// ============================================================================
// KANBAN BOARD SLICE
// ============================================================================

export interface KanbanSlice {
  // State
  kanbanBoard: KanbanBoard | null;
  kanbanConfig: KanbanConfig;
  kanbanFilter: KanbanFilter;
  selectedCardId: string | null;

  // Actions - Board management
  initializeKanban: () => void;
  syncFromPertTasks: () => void;

  // Actions - Card CRUD
  addCard: (status: KanbanStatus, card: Partial<KanbanCard>) => void;
  updateCard: (id: string, updates: Partial<KanbanCard>) => void;
  deleteCard: (id: string) => void;
  duplicateCard: (id: string) => void;

  // Actions - Card movement
  moveCard: (result: DragResult) => void;
  moveCardToStatus: (cardId: string, newStatus: KanbanStatus) => void;

  // Actions - Card details
  updateCardPriority: (id: string, priority: KanbanPriority) => void;
  assignCard: (id: string, memberIds: string[]) => void;
  addChecklistItem: (cardId: string, text: string) => void;
  toggleChecklistItem: (cardId: string, itemId: string) => void;
  deleteChecklistItem: (cardId: string, itemId: string) => void;

  // Actions - Configuration
  updateKanbanConfig: (config: Partial<KanbanConfig>) => void;
  resetKanbanConfig: () => void;

  // Actions - Filtering & Selection
  setKanbanFilter: (filter: Partial<KanbanFilter>) => void;
  clearKanbanFilter: () => void;
  selectCard: (id: string | null) => void;

  // Computed
  getCardsByStatus: (status: KanbanStatus) => KanbanCard[];
  getFilteredCards: () => KanbanCard[];
  getCardById: (id: string) => KanbanCard | undefined;
  getKanbanStatistics: () => KanbanStatistics;
  checkWipLimit: (status: KanbanStatus) => boolean;
}

export const createKanbanSlice: StateCreator<any, [], [], KanbanSlice> = (set, get) => {
  const canEdit = (): boolean => {
    const state = get();
    const role = state.currentProject?.currentUserRole;
    if (role) {
      const allowed = role === 'OWNER' || role === 'ADMIN' || role === 'EDITOR';
      if (!allowed) {
        state.addNotification?.('warning', 'Droits insuffisants pour modifier ce contenu.');
      }
      return allowed;
    }
    if (state.storageMode === 'backend') {
      state.addNotification?.('warning', 'Droits insuffisants pour modifier ce contenu.');
      return false;
    }
    return true;
  };

  return ({
  // Initial state
  kanbanBoard: null,
  kanbanConfig: DEFAULT_KANBAN_CONFIG,
  kanbanFilter: {},
  selectedCardId: null,

  // Initialize Kanban board
  initializeKanban: () => {
    if (!canEdit()) return;
    const { currentProject, kanbanBoard } = get();

    if (!currentProject) {
      get().addNotification('error', 'Aucun projet actif');
      return;
    }

    // Only initialize if no board exists
    if (!kanbanBoard || kanbanBoard.projectId !== currentProject.id) {
      const newBoard: KanbanBoard = {
        id: `kanban-${currentProject.id}`,
        projectId: currentProject.id,
        columns: KANBAN_COLUMNS.map((col) => ({
          id: col.id,
          title: col.title,
          cards: [],
          wipLimit: get().kanbanConfig.columns[col.id].wipLimit,
          color: col.color,
          order: col.order,
        })),
        lastUpdated: new Date(),
        createdAt: new Date(),
      };

      set({ kanbanBoard: newBoard });
      get().addNotification('success', 'Kanban initialisé');
    }
  },

  // Sync cards from PERT tasks
  syncFromPertTasks: () => {
    if (!canEdit()) return;
    const { nodes, kanbanBoard, currentProject } = get();

    if (!currentProject) {
      get().addNotification('error', 'Aucun projet actif');
      return;
    }

    if (!kanbanBoard) {
      get().initializeKanban();
    }

    const board = get().kanbanBoard;
    if (!board) return;

    // Get existing card IDs linked to PERT nodes
    const existingCardPertIds = new Set<string>();
    board.columns.forEach((col: KanbanColumn) => {
      col.cards.forEach((card: KanbanCard) => {
        if (card.pertNodeId) {
          existingCardPertIds.add(card.pertNodeId);
        }
      });
    });

    // Create cards for PERT nodes that don't have cards yet
    const newCards: KanbanCard[] = [];
    nodes.forEach((node: any) => {
      if (!existingCardPertIds.has(node.id)) {
        // Determine status based on progress
        let status: KanbanStatus = 'todo';
        if (node.progress === 100) {
          status = 'done';
        } else if (node.progress > 0) {
          status = 'in_progress';
        }

        const card: KanbanCard = {
          id: `card-${Date.now()}-${node.id}`,
          title: node.name,
          description: node.description,
          status,
          priority: node.isCritical ? 'urgent' : 'medium',
          pertNodeId: node.id,
          estimatedHours: node.duration,
          assignedTo: node.resources?.map((r: Resource) => r.name) || [],
          tags: node.isCritical ? ['critical-path'] : [],
          createdAt: new Date(),
          updatedAt: new Date(),
          order: 0,
        };

        newCards.push(card);
      }
    });

    if (newCards.length > 0) {
      const updatedColumns = board.columns.map((col: KanbanColumn) => {
        const relevantCards = newCards.filter((card: KanbanCard) => card.status === col.id);
        if (relevantCards.length > 0) {
          return {
            ...col,
            cards: [...col.cards, ...relevantCards],
          };
        }
        return col;
      });

      const updatedBoard = {
        ...board,
        columns: updatedColumns,
        lastUpdated: new Date(),
      };

      set({ kanbanBoard: updatedBoard });
      get().addNotification('success', `${newCards.length} tâches synchronisées depuis PERT`);
    } else {
      get().addNotification('info', 'Toutes les tâches PERT sont déjà synchronisées');
    }
  },

  // Add new card
  addCard: (status, cardData) => {
    if (!canEdit()) return;
    const { kanbanBoard, currentProject } = get();

    if (!currentProject) {
      get().addNotification('error', 'Aucun projet actif');
      return;
    }

    if (!kanbanBoard) {
      get().initializeKanban();
    }

    const board = get().kanbanBoard;
    if (!board) return;

    const newCard: KanbanCard = {
      id: `card-${Date.now()}`,
      title: cardData.title || 'Nouvelle tâche',
      description: cardData.description,
      status,
      priority: cardData.priority || 'medium',
      assignedTo: cardData.assignedTo || [],
      estimatedHours: cardData.estimatedHours,
      actualHours: cardData.actualHours,
      dueDate: cardData.dueDate,
      pertNodeId: cardData.pertNodeId,
      tags: cardData.tags || [],
      checklist: cardData.checklist || [],
      createdAt: new Date(),
      updatedAt: new Date(),
      order: board.columns.find((c: any) =>c.id === status)?.cards.length || 0,
      color: cardData.color,
      blockedBy: cardData.blockedBy,
      blockers: cardData.blockers,
    };

    const updatedColumns = board.columns.map((col: any) => {
      if (col.id === status) {
        return {
          ...col,
          cards: [...col.cards, newCard],
        };
      }
      return col;
    });

    const updatedBoard = {
      ...board,
      columns: updatedColumns,
      lastUpdated: new Date(),
    };

    set({ kanbanBoard: updatedBoard });
    get().addNotification('success', `Carte "${newCard.title}" ajoutée`);
  },

  // Update card
  updateCard: (id, updates) => {
    if (!canEdit()) return;
    const { kanbanBoard } = get();
    if (!kanbanBoard) return;

    let cardFound = false;
    const updatedColumns = kanbanBoard.columns.map((col: any) => {
      const cardIndex = col.cards.findIndex((card: any) =>card.id === id);
      if (cardIndex !== -1) {
        const updatedCards = [...col.cards];
        updatedCards[cardIndex] = {
          ...updatedCards[cardIndex],
          ...updates,
          updatedAt: new Date(),
        };
        cardFound = true;
        return {
          ...col,
          cards: updatedCards,
        };
      }
      return col;
    });

    if (cardFound) {
      const updatedBoard = {
        ...kanbanBoard,
        columns: updatedColumns,
        lastUpdated: new Date(),
      };
      set({ kanbanBoard: updatedBoard });
      get().addNotification('success', 'Carte mise à jour');
    } else {
      get().addNotification('error', 'Carte introuvable');
    }
  },

  // Delete card
  deleteCard: (id) => {
    if (!canEdit()) return;
    const { kanbanBoard } = get();
    if (!kanbanBoard) return;

    const updatedColumns = kanbanBoard.columns.map((col: any) => ({
      ...col,
      cards: col.cards.filter((card: any) => card.id !== id),
    }));

    const updatedBoard = {
      ...kanbanBoard,
      columns: updatedColumns,
      lastUpdated: new Date(),
    };

    set({ kanbanBoard: updatedBoard });
    get().addNotification('success', 'Carte supprimée');
  },

  // Duplicate card
  duplicateCard: (id) => {
    if (!canEdit()) return;
    const card = get().getCardById(id);
    if (!card) {
      get().addNotification('error', 'Carte introuvable');
      return;
    }

    get().addCard(card.status, {
      ...card,
      id: undefined,
      title: `${card.title} (Copie)`,
      createdAt: new Date(),
      updatedAt: new Date(),
    });
  },

  // Move card (drag & drop)
  moveCard: (result) => {
    if (!canEdit()) return;
    const { kanbanBoard, kanbanConfig } = get();
    if (!kanbanBoard) return;

    const { cardId, sourceStatus, destinationStatus, destinationIndex } = result;

    // Check WIP limit
    if (
      kanbanConfig.enableWipLimits &&
      sourceStatus !== destinationStatus &&
      !get().checkWipLimit(destinationStatus)
    ) {
      get().addNotification(
        'warning',
        `Limite WIP atteinte pour ${kanbanConfig.columns[destinationStatus].title}`
      );
      return;
    }

    let card: KanbanCard | undefined;

    // Remove from source
    const updatedColumns = kanbanBoard.columns.map((col: any) => {
      if (col.id === sourceStatus) {
        const cardIndex = col.cards.findIndex((c: any) =>c.id === cardId);
        if (cardIndex !== -1) {
          card = col.cards[cardIndex];
          return {
            ...col,
            cards: col.cards.filter((c: any) =>c.id !== cardId),
          };
        }
      }
      return col;
    });

    if (!card) {
      get().addNotification('error', 'Carte introuvable');
      return;
    }

    // Add to destination
    const finalColumns = updatedColumns.map((col: any) => {
      if (col.id === destinationStatus) {
        const newCards = [...col.cards];
        const updatedCard = {
          ...card!,
          status: destinationStatus,
          updatedAt: new Date(),
        };
        newCards.splice(destinationIndex, 0, updatedCard);
        return {
          ...col,
          cards: newCards.map((c, idx) => ({ ...c, order: idx })),
        };
      }
      return col;
    });

    const updatedBoard = {
      ...kanbanBoard,
      columns: finalColumns,
      lastUpdated: new Date(),
    };

    set({ kanbanBoard: updatedBoard });
  },

  // Move card to status
  moveCardToStatus: (cardId, newStatus) => {
    if (!canEdit()) return;
    const card = get().getCardById(cardId);
    if (!card) return;

    const currentCards = get().getCardsByStatus(newStatus);
    const destinationIndex = currentCards.length;

    get().moveCard({
      cardId,
      sourceStatus: card.status,
      destinationStatus: newStatus,
      sourceIndex: card.order,
      destinationIndex,
    });
  },

  // Update card priority
  updateCardPriority: (id, priority) => {
    if (!canEdit()) return;
    get().updateCard(id, { priority });
  },

  // Assign card
  assignCard: (id, memberIds) => {
    if (!canEdit()) return;
    get().updateCard(id, { assignedTo: memberIds });
    get().addNotification('success', 'Carte assignée');
  },

  // Add checklist item
  addChecklistItem: (cardId, text) => {
    if (!canEdit()) return;
    const card = get().getCardById(cardId);
    if (!card) {
      get().addNotification('error', 'Carte introuvable');
      return;
    }

    const newItem: KanbanChecklistItem = {
      id: `checklist-${Date.now()}`,
      text,
      completed: false,
      createdAt: new Date(),
    };

    get().updateCard(cardId, {
      checklist: [...(card.checklist || []), newItem],
    });

    get().addNotification('success', 'Élément ajouté à la checklist');
  },

  // Toggle checklist item
  toggleChecklistItem: (cardId, itemId) => {
    if (!canEdit()) return;
    const card = get().getCardById(cardId);
    if (!card || !card.checklist) return;

    const updatedChecklist = card.checklist.map((item: any) =>
      item.id === itemId ? { ...item, completed: !item.completed } : item
    );

    get().updateCard(cardId, { checklist: updatedChecklist });
  },

  // Delete checklist item
  deleteChecklistItem: (cardId, itemId) => {
    if (!canEdit()) return;
    const card = get().getCardById(cardId);
    if (!card || !card.checklist) return;

    const updatedChecklist = card.checklist.filter((item: any) =>item.id !== itemId);
    get().updateCard(cardId, { checklist: updatedChecklist });
  },

  // Update configuration
  updateKanbanConfig: (config) => {
    if (!canEdit()) return;
    set((state: any) => ({
      kanbanConfig: {
        ...state.kanbanConfig,
        ...config,
      },
    }));
  },

  // Reset configuration
  resetKanbanConfig: () => {
    if (!canEdit()) return;
    set({ kanbanConfig: DEFAULT_KANBAN_CONFIG });
    get().addNotification('info', 'Configuration réinitialisée');
  },

  // Set filter
  setKanbanFilter: (filter) => {
    set((state: any) => ({
      kanbanFilter: {
        ...state.kanbanFilter,
        ...filter,
      },
    }));
  },

  // Clear filter
  clearKanbanFilter: () => {
    set({ kanbanFilter: {} });
  },

  // Select card
  selectCard: (id) => {
    set({ selectedCardId: id });
  },

  // Get cards by status
  getCardsByStatus: (status) => {
    const { kanbanBoard } = get();
    if (!kanbanBoard) return [];

    const column = kanbanBoard.columns.find((col: any) => col.id === status);
    return column?.cards || [];
  },

  // Get filtered cards
  getFilteredCards: () => {
    const { kanbanBoard, kanbanFilter } = get();
    if (!kanbanBoard) return [];

    let allCards: KanbanCard[] = [];
    kanbanBoard.columns.forEach((col: any) => {
      allCards = [...allCards, ...col.cards];
    });

    // Apply assignee filter
    if (kanbanFilter.assignees && kanbanFilter.assignees.length > 0) {
      allCards = allCards.filter((card) =>
        card.assignedTo?.some((id) => kanbanFilter.assignees!.includes(id))
      );
    }

    // Apply priority filter
    if (kanbanFilter.priorities && kanbanFilter.priorities.length > 0) {
      allCards = allCards.filter((card: any) =>kanbanFilter.priorities!.includes(card.priority));
    }

    // Apply tag filter
    if (kanbanFilter.tags && kanbanFilter.tags.length > 0) {
      allCards = allCards.filter((card) =>
        kanbanFilter.tags!.some((tag: any) =>card.tags.includes(tag))
      );
    }

    // Apply search query
    if (kanbanFilter.searchQuery && kanbanFilter.searchQuery.trim().length > 0) {
      const query = kanbanFilter.searchQuery.toLowerCase();
      allCards = allCards.filter(
        (card) =>
          card.title.toLowerCase().includes(query) ||
          card.description?.toLowerCase().includes(query) ||
          card.tags.some((tag: any) =>tag.toLowerCase().includes(query))
      );
    }

    // Apply due date filter
    if (kanbanFilter.dueDate) {
      allCards = allCards.filter((card: any) =>{
        if (!card.dueDate) return false;
        const cardDate = new Date(card.dueDate);
        if (kanbanFilter.dueDate!.from && cardDate < kanbanFilter.dueDate!.from) return false;
        if (kanbanFilter.dueDate!.to && cardDate > kanbanFilter.dueDate!.to) return false;
        return true;
      });
    }

    return allCards;
  },

  // Get card by ID
  getCardById: (id) => {
    const { kanbanBoard } = get();
    if (!kanbanBoard) return undefined;

    for (const col of kanbanBoard.columns) {
      const card = col.cards.find((c: any) =>c.id === id);
      if (card) return card;
    }
    return undefined;
  },

  // Get Kanban statistics
  getKanbanStatistics: () => {
    const { kanbanBoard } = get();

    if (!kanbanBoard) {
      return {
        totalCards: 0,
        byStatus: {
          backlog: 0,
          todo: 0,
          in_progress: 0,
          review: 0,
          done: 0,
        },
        byPriority: { low: 0, medium: 0, high: 0, urgent: 0 },
        completionRate: 0,
        cardsOverdue: 0,
        cardsBlocked: 0,
        totalEstimatedHours: 0,
        totalActualHours: 0,
      };
    }

    let allCards: KanbanCard[] = [];
    kanbanBoard.columns.forEach((col: any) => {
      allCards = [...allCards, ...col.cards];
    });

    // Count by status
    const byStatus: Record<KanbanStatus, number> = {
      backlog: 0,
      todo: 0,
      in_progress: 0,
      review: 0,
      done: 0,
    };
    kanbanBoard.columns.forEach((col: any) => {
      byStatus[col.id as KanbanStatus] = col.cards.length;
    });

    // Count by priority
    const byPriority: Record<KanbanPriority, number> = {
      low: 0,
      medium: 0,
      high: 0,
      urgent: 0,
    };
    allCards.forEach((card: any) => {
      byPriority[card.priority as KanbanPriority]++;
    });

    // Calculate completion rate
    const totalCards = allCards.length;
    const doneCards = byStatus.done;
    const completionRate = totalCards > 0 ? (doneCards / totalCards) * 100 : 0;

    // Count overdue cards
    const now = new Date();
    const cardsOverdue = allCards.filter(
      (card) => card.dueDate && new Date(card.dueDate) < now && card.status !== 'done'
    ).length;

    // Count blocked cards
    const cardsBlocked = allCards.filter((card: any) =>card.blockedBy && card.blockedBy.length > 0)
      .length;

    // Calculate total hours
    const totalEstimatedHours = allCards.reduce((sum, card) => sum + (card.estimatedHours || 0), 0);
    const totalActualHours = allCards.reduce((sum, card) => sum + (card.actualHours || 0), 0);

    return {
      totalCards,
      byStatus,
      byPriority,
      completionRate,
      cardsOverdue,
      cardsBlocked,
      totalEstimatedHours,
      totalActualHours,
    };
  },

  // Check WIP limit
  checkWipLimit: (status) => {
    const { kanbanBoard, kanbanConfig } = get();
    if (!kanbanBoard || !kanbanConfig.enableWipLimits) return true;

    const column = kanbanBoard.columns.find((col: any) => col.id === status);
    if (!column || !column.wipLimit) return true;

    return column.cards.length < column.wipLimit;
  },
  });
};
